/**
 * Application Constants
 *
 * Centralized configuration values for DEED-AI frontend.
 */

// Application info
export const APP_NAME = 'DEED-AI';
export const APP_VERSION = '1.0.0';
export const APP_DESCRIPTION = 'Minnesota PFML Compliance Management';

// Minnesota PFML program constants
export const PFML = {
  // Premium rates (effective 2026)
  STANDARD_RATE: 0.0088, // 0.88%
  SMALL_EMPLOYER_RATE: 0.0066, // 0.66% for ≤30 employees
  SMALL_EMPLOYER_THRESHOLD: 30,

  // Rate split
  EMPLOYER_SHARE: 0.50, // 50%
  EMPLOYEE_SHARE: 0.50, // 50%

  // Leave limits (in weeks)
  FAMILY_LEAVE_MAX: 12,
  MEDICAL_LEAVE_MAX: 12,
  COMBINED_LEAVE_MAX: 20,

  // Official MN Paid Leave Values (updated annually in October)
  // Source: https://pl.mn.gov/resources/calculators/estimate-your-payments
  SAWW: 1423, // State Average Weekly Wage (maximum weekly benefit)
  SAAW: 73989, // State Average Annual Wage
  HALF_SAWW: 711.50, // 50% of SAWW (bracket threshold)
  WEEKS_PER_QUARTER: 13, // Weeks per quarter for AWW calculation

  // Eligibility
  MIN_EARNINGS_MULTIPLIER: 0.053, // 5.3% of SAAW required for eligibility
  MINIMUM_EARNINGS: 3921, // ~$3,921 (0.053 * 73989) minimum annual wages

  // Benefit calculation - tiered percentages based on wage brackets
  BENEFIT_RATE_TIER_1: 0.90, // 90% for wages $0 - $711.50 (up to half SAWW)
  BENEFIT_RATE_TIER_2: 0.66, // 66% for wages $711.50 - $1,423 (half SAWW to SAWW)
  BENEFIT_RATE_TIER_3: 0.55, // 55% for wages above $1,423 (above SAWW)

  // Legacy aliases for backwards compatibility
  BENEFIT_PERCENTAGE_BELOW_50: 0.90,
  BENEFIT_PERCENTAGE_ABOVE_50: 0.66,

  // Program dates
  PROGRAM_START_DATE: '2026-01-01',
  PREMIUM_COLLECTION_START: '2026-01-01',
  BENEFIT_START_DATE: '2026-01-01',
};

// Leave types with labels and descriptions for views
export const LEAVE_TYPES = {
  FAMILY: {
    value: 'family',
    label: 'Family Leave',
    description: 'Care for a family member with a serious health condition.',
  },
  MEDICAL: {
    value: 'medical',
    label: 'Medical Leave',
    description: 'Recovery from your own serious health condition.',
  },
  BONDING: {
    value: 'bonding',
    label: 'Bonding Leave',
    description: 'Bond with a new child through birth, adoption, or foster care.',
  },
  CAREGIVING: {
    value: 'caregiving',
    label: 'Caregiving Leave',
    description: 'Provide care for a family member with ongoing health needs.',
  },
  MILITARY: {
    value: 'military',
    label: 'Military Family Leave',
    description: 'Address qualifying exigencies related to a family member\'s military service.',
  },
  SAFE_TIME: {
    value: 'safe_time',
    label: 'Safe Time Leave',
    description: 'Address issues related to domestic abuse, sexual assault, or stalking.',
  },
};

// Leave type values for API usage
export const LEAVE_TYPE_VALUES = {
  FAMILY: 'family',
  MEDICAL: 'medical',
  BONDING: 'bonding',
  CAREGIVING: 'caregiving',
  MILITARY: 'military',
  SAFE_TIME: 'safe_time',
};

export const LEAVE_TYPE_LABELS = {
  family: 'Family Leave',
  medical: 'Medical Leave',
  bonding: 'Bonding Leave',
  caregiving: 'Caregiving Leave',
  military: 'Military Family Leave',
  safe_time: 'Safe Time Leave',
};

// Leave statuses
export const LEAVE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  DENIED: 'denied',
  CANCELLED: 'cancelled',
  ON_LEAVE: 'on_leave',
  COMPLETED: 'completed',
};

export const LEAVE_STATUS_LABELS = {
  [LEAVE_STATUS.DRAFT]: 'Draft',
  [LEAVE_STATUS.PENDING]: 'Pending Review',
  [LEAVE_STATUS.APPROVED]: 'Approved',
  [LEAVE_STATUS.DENIED]: 'Denied',
  [LEAVE_STATUS.CANCELLED]: 'Cancelled',
  [LEAVE_STATUS.ON_LEAVE]: 'On Leave',
  [LEAVE_STATUS.COMPLETED]: 'Completed',
};

export const LEAVE_STATUS_COLORS = {
  [LEAVE_STATUS.DRAFT]: 'gray',
  [LEAVE_STATUS.PENDING]: 'yellow',
  [LEAVE_STATUS.APPROVED]: 'green',
  [LEAVE_STATUS.DENIED]: 'red',
  [LEAVE_STATUS.CANCELLED]: 'gray',
  [LEAVE_STATUS.ON_LEAVE]: 'blue',
  [LEAVE_STATUS.COMPLETED]: 'green',
};

// User roles
export const USER_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
};

export const USER_ROLE_LABELS = {
  [USER_ROLES.OWNER]: 'Business Owner',
  [USER_ROLES.ADMIN]: 'Administrator',
  [USER_ROLES.EMPLOYEE]: 'Employee',
};

// Report types
export const REPORT_TYPES = {
  WAGE_DETAIL: 'wage_detail',
  PREMIUM_SUMMARY: 'premium_summary',
  LEAVE_SUMMARY: 'leave_summary',
  COMPLIANCE: 'compliance',
};

export const REPORT_FORMAT = {
  CSV: 'csv',
  PDF: 'pdf',
};

// Quarters for reporting
export const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

// Fraud alert severities
export const ALERT_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

export const ALERT_SEVERITY_COLORS = {
  [ALERT_SEVERITY.LOW]: 'blue',
  [ALERT_SEVERITY.MEDIUM]: 'yellow',
  [ALERT_SEVERITY.HIGH]: 'orange',
  [ALERT_SEVERITY.CRITICAL]: 'red',
};

// Staffing coverage thresholds
export const COVERAGE_THRESHOLDS = {
  CRITICAL: 0.50, // <50%
  HIGH: 0.70, // 50-70%
  MEDIUM: 0.80, // 70-80%
  LOW: 0.90, // 80-90%
  NONE: 1.00, // >90%
};

// API endpoints (for non-Firebase services)
export const API_ENDPOINTS = {
  QUICKBOOKS_AUTH: '/api/integrations/quickbooks/auth',
  QUICKBOOKS_CALLBACK: '/api/integrations/quickbooks/callback',
  QUICKBOOKS_SYNC: '/api/integrations/quickbooks/sync',
};

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  DISPLAY_FULL: 'MMMM d, yyyy',
  INPUT: 'yyyy-MM-dd',
  API: 'yyyy-MM-dd',
  DATETIME: 'MMM d, yyyy h:mm a',
  TIME: 'h:mm a',
  QUARTER: 'yyyy-Qq',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 100,
};

// Validation
export const VALIDATION = {
  SSN_PATTERN: /^\d{3}-?\d{2}-?\d{4}$/,
  EIN_PATTERN: /^\d{2}-?\d{7}$/,
  PHONE_PATTERN: /^\+?1?\d{10,14}$/,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  ZIP_PATTERN: /^\d{5}(-\d{4})?$/,
  MIN_PASSWORD_LENGTH: 8,
  MAX_NAME_LENGTH: 100,
  MAX_ADDRESS_LENGTH: 200,
  MAX_NOTES_LENGTH: 1000,
};

// Error messages
export const ERROR_MESSAGES = {
  GENERIC: 'An unexpected error occurred. Please try again.',
  NETWORK: 'Unable to connect. Please check your internet connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  VALIDATION: 'Please correct the errors and try again.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'A server error occurred. Please try again later.',
};

// Success messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully.',
  CREATED: 'Created successfully.',
  UPDATED: 'Updated successfully.',
  DELETED: 'Deleted successfully.',
  SUBMITTED: 'Submitted successfully.',
  APPROVED: 'Approved successfully.',
  DENIED: 'Request has been denied.',
};

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'deed_auth_token',
  USER_PREFERENCES: 'deed_user_prefs',
  THEME: 'deed_theme',
  SIDEBAR_COLLAPSED: 'deed_sidebar_collapsed',
  LAST_VIEWED_ORG: 'deed_last_org',
  ONBOARDING_COMPLETE: 'deed_onboarding_complete',
};

// Feature flags
export const FEATURES = {
  QUICKBOOKS_INTEGRATION: process.env.REACT_APP_FEATURE_QUICKBOOKS === 'true',
  AI_CHATBOT: process.env.REACT_APP_FEATURE_CHATBOT === 'true',
  FRAUD_DETECTION: process.env.REACT_APP_FEATURE_FRAUD === 'true',
  STAFFING_FORECAST: process.env.REACT_APP_FEATURE_FORECAST === 'true',
  DARK_MODE: process.env.REACT_APP_FEATURE_DARK_MODE === 'true',
};

// Premium rates (convenience export for views)
export const PREMIUM_RATES = {
  STANDARD: 0.88, // As percentage
  SMALL_EMPLOYER: 0.66, // As percentage
};

// Leave limits in weeks (convenience export for views)
export const LEAVE_LIMITS = {
  FAMILY_WEEKS: PFML.FAMILY_LEAVE_MAX,
  MEDICAL_WEEKS: PFML.MEDICAL_LEAVE_MAX,
  COMBINED_MAX_WEEKS: PFML.COMBINED_LEAVE_MAX,
};

// Employee statuses
export const EMPLOYEE_STATUSES = {
  ACTIVE: { value: 'active', label: 'Active', color: 'green' },
  INACTIVE: { value: 'inactive', label: 'Inactive', color: 'gray' },
  ON_LEAVE: { value: 'on_leave', label: 'On Leave', color: 'blue' },
  TERMINATED: { value: 'terminated', label: 'Terminated', color: 'red' },
};

// Request statuses (alias for leave statuses in request views)
export const REQUEST_STATUSES = {
  PENDING: { value: 'pending', label: 'Pending', color: 'yellow' },
  APPROVED: { value: 'approved', label: 'Approved', color: 'green' },
  DENIED: { value: 'denied', label: 'Denied', color: 'red' },
  CANCELLED: { value: 'cancelled', label: 'Cancelled', color: 'gray' },
};
